import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { LinkIcon } from '@utils/icons';
import type { LocationStatus } from '@interfaces/IScheduler';
import { StatusGlyph } from './StatusGlyph';
import { LOCATION_STATUS_LABEL_KEY, nextAreaAction } from '../../SchedulerPage/lib/locationStatusLabels';
import { matrixCellPropsEqual, type IAreaEditor, type IMatrixCellProps } from './matrixCellPropsEqual';
import styles from './matrixCell.module.css';

// Area status → the vivid full-fill colour class.
const STATUS_CLASS: Record<LocationStatus, string> = {
  unplanned: styles.planned as string,
  planned: styles.planned as string,
  inProgress: styles.inProgress as string,
  delayed: styles.blocked as string,
  delayedOverdue: styles.blocked as string,
  overdue: styles.blocked as string,
  readyForReview: styles.review as string,
  complete: styles.completed as string,
};

/** Floor fill bucket by rolled-up %: 0 → neutral, 1–99 → amber, 100 → green. */
function floorBucketClass(percent: number): string {
  if (percent >= 100) return styles.floorDone as string;
  if (percent > 0) return styles.floorProgress as string;
  return styles.floorIdle as string;
}

/** What a tap on this area cell does for this user (`nextAreaAction`) — or
 *  `null`: an `unplanned` area (its task is not committed), a done one for a
 *  Limited user, any area for a user who reports nothing. */
function areaTap(editor: IAreaEditor, status: LocationStatus, taskId: string, zoneId: string): (() => void) | null {
  const action = nextAreaAction(status, editor.tier);
  if (action === null) return null;
  return action === 'review' ? () => editor.review(taskId, zoneId) : () => editor.write(taskId, zoneId, action);
}

export const MatrixCell = memo(function MatrixCell({
  cell,
  variant = 'area',
  rowKey,
  colKey,
  editable,
  zoneId,
  areaEditor,
  gridColumn,
  gridRow,
}: IMatrixCellProps) {
  const { t } = useTranslation();
  const classes = [styles.cell];
  if (cell) classes.push(styles.filled);
  else classes.push(styles.empty); // no activity here — hatched, distinct from a real 0%
  if (variant === 'floor') classes.push(styles.floorCell);
  const className = classes.join(' ');
  const style = gridColumn !== undefined || gridRow !== undefined ? { gridColumn, gridRow } : undefined;

  if (!cell) return <div className={className} style={style} data-row={rowKey} data-col={colKey} />;

  const coupled = cell.rtlsCoupled && (
    <span className={styles.coupled} aria-label={t('activities.tracker.coupled')}>
      <LinkIcon size={12} />
    </span>
  );

  // Floor (rollup) rows: a full-cell two-tone progress fill to the %. Area (leaf)
  // rows: a full-cell solid coloured by STATUS with just the mark (label on hover).
  let inner;
  if (variant === 'floor') {
    inner = (
      <div className={`${styles.floorFill} ${floorBucketClass(cell.percent)}`}>
        <div className={styles.floorBar} style={{ width: `${cell.percent}%` }} />
        <span className={styles.floorPct}>{cell.percent}%</span>
        {coupled}
      </div>
    );
  } else {
    const status = cell.status ?? 'unplanned';
    const label = t(LOCATION_STATUS_LABEL_KEY[status]);
    const mark = (
      <>
        <StatusGlyph status={status} size={14} />
        {coupled}
      </>
    );
    const fillClass = `${styles.status} ${STATUS_CLASS[status]}`;
    // A cell with no tap for this user is a plain fill, not a button.
    const onTap = editable && areaEditor && zoneId && colKey ? areaTap(areaEditor, status, colKey, zoneId) : null;
    inner = onTap ? (
      <button type="button" className={`${fillClass} ${styles.editable}`} title={label} aria-label={label} onClick={onTap}>
        {mark}
      </button>
    ) : (
      <div className={fillClass} title={label} aria-label={label}>
        {mark}
      </div>
    );
  }

  return (
    <div className={className} style={style} data-row={rowKey} data-col={colKey}>
      {inner}
    </div>
  );
}, matrixCellPropsEqual);
